import { useEffect } from 'react';
import { http } from '../core/http/client';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppSelector } from './store';
import { ROUTES } from './routes';
import { FullPageLoader } from '../components/FullPageLoader';

/**
 * Route guard: every protected route lives under this element. The token is
 * kept outside Redux (see tokenStorage); the Redux session status mirrors it.
 */
export function RequireAuth() {
  const status = useAppSelector((state) => state.session.status);
  const location = useLocation();

  useEffect(() => {
    if (status !== 'authenticated') return;
    let pending = false;
    const check = async () => {
      if (pending) return;
      pending = true;
      try { await http().get('/auth/session-status', { skipErrorToast: true }); }
      catch { /* HTTP client handles session expiry. Retry transient failures later. */ }
      finally { pending = false; }
    };
    void check();
    const interval = window.setInterval((): void => { void check(); }, 15000);
    window.addEventListener('focus', check);
    return () => { window.clearInterval(interval); window.removeEventListener('focus', check); };
  }, [status]);

  if (status === 'initializing') {
    return <FullPageLoader label="Checking your session" />;
  }

  if (status !== 'authenticated') {
    const from = location.pathname + location.search;
    return <Navigate to={ROUTES.login} replace state={{ from: from === ROUTES.login ? ROUTES.home : from }} />;
  }

  return <Outlet />;
}
